/**
 * Duplicate Property Component (Wrapper)
 *
 * This is a minimal wrapper around the generic DuplicateEntity component
 * that handles property-specific mutation logic.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client/react'
import { enqueueSnackbar } from 'notistack'
import DuplicateEntityComponent from './index.jsx'
import { PROPERTY_CONFIG } from './entityConfigs'
import { COPY_PROPERTY } from '@/housefolios-components/Property/mutations'
import { GET_PROPERTIES } from '@/housefolios-components/Properties/queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'

/**
 * Duplicate Property Component
 *
 * @param {Object} props - Component props
 * @param {string} props.propertyId - ID of the property to duplicate
 * @param {Object} props.property - The property object
 * @param {Array} props.portfolios - Available portfolios
 * @param {string} props.selectedPortfolio - Currently selected portfolio
 * @param {Function} props.setSelected - Function to clear selection after duplication
 * @param {Object} props.session - User session object
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onError - Error callback
 * @param {boolean} props.notifications - Whether to show as notification menu item
 * @param {boolean} props.rightClickMenu - Whether to show as right-click menu item
 * @param {boolean} props.underwriting - Whether this is for underwriting context
 * @param {string} props.buttonText - Custom button text
 */
function DuplicateProperty(props) {
  const {
    propertyId,
    property,
    portfolios,
    selectedPortfolio: initialSelectedPortfolio,
    setSelected,
    session,
    onSuccess,
    onError,
    notifications = false,
    rightClickMenu = false,
    underwriting = false,
    buttonText = '',
    ...otherProps
  } = props

  // Local state for portfolio selection
  const [selectedPortfolio, setSelectedPortfolio] = useState(
    initialSelectedPortfolio,
  )

  // GraphQL mutation
  const [copyProperty] = useMutation(COPY_PROPERTY)

  // Handle form submission
  const handleSubmit = async (additionalState = {}) => {
    if (!selectedPortfolio) {
      enqueueSnackbar('Please select a portfolio', {
        variant: 'warning',
      })
      return
    }

    try {
      const result = await copyProperty({
        awaitRefetchQueries: true,
        variables: {
          propertyId: propertyId,
          portfolioId: selectedPortfolio,
        },
        refetchQueries: [
          {
            query: GET_PROPERTIES,
            variables: {
              portfolioId: [selectedPortfolio],
              limit: GET_PROPERTIES_QUERY_LIMIT,
            },
          },
          {
            query: GET_PROPERTIES,
            variables: {
              portfolioId: portfolios.map((p) => p._id),
              limit: GET_PROPERTIES_QUERY_LIMIT,
            },
          },
          { query: GET_PORTFOLIOS },
          { query: GET_ME },
        ],
      })

      // Success handling
      enqueueSnackbar('Property was duplicated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      })

      if (setSelected) setSelected([])
      if (onSuccess) onSuccess(result)
    } catch (error) {
      // Error handling
      console.error('Failed to duplicate property:', error)
      enqueueSnackbar('Property failed to duplicate', {
        variant: 'error',
        autoHideDuration: 3000,
      })

      if (onError) onError(error)
    }
  }

  const { limits = {}, activeOrg } = session.me
  const { monthlyCount, bonusProperties = 0 } =
    activeOrg.propertyCount
  const LIMIT = limits.propertyLimit + bonusProperties
  const overLimit = monthlyCount + 1 > LIMIT

  return (
    <DuplicateEntityComponent
      {...otherProps}
      entityType="property"
      entityId={propertyId}
      entity={property}
      portfolios={portfolios}
      selectedPortfolio={selectedPortfolio}
      setSelected={setSelected}
      session={session}
      config={PROPERTY_CONFIG}
      onSuccess={onSuccess}
      onError={onError}
      onSubmit={handleSubmit}
      onPortfolioChange={setSelectedPortfolio}
      notifications={notifications}
      rightClickMenu={rightClickMenu}
      sidebarMenu={false} // Properties don't typically use sidebar menu
      underwriting={underwriting}
      buttonText={buttonText}
      overLimit={overLimit}
    />
  )
}

// PropTypes - including rendering props
DuplicateProperty.propTypes = {
  propertyId: PropTypes.string.isRequired,
  property: PropTypes.object,
  portfolios: PropTypes.array.isRequired,
  selectedPortfolio: PropTypes.string,
  setSelected: PropTypes.func,
  session: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  notifications: PropTypes.bool,
  rightClickMenu: PropTypes.bool,
  underwriting: PropTypes.bool,
  buttonText: PropTypes.string,
}

export default DuplicateProperty
